import { forwardRef } from 'react';

import { compose, cva } from 'cva';
import {
  Heading as RACHeading,
  type HeadingProps as RACHeadingProps,
} from 'react-aria-components/Heading';

import { textColorVariants } from '../text/text';

import styles from './heading.module.css';

export const headingVariantVariants = cva({
  base: styles.heading,
  variants: {
    level: {
      1: styles.level_h1,
      2: styles.level_h2,
      3: styles.level_h3,
      4: styles.level_h4,
      5: styles.level_h5,
      6: styles.level_h6,
    },
    noWrap: {
      true: styles.no_wrap,
    },
  },
  defaultVariants: {
    level: 2,
    noWrap: false,
  },
});

export const headingVariants = compose(
  headingVariantVariants,
  textColorVariants,
);

export type HeadingProps = RACHeadingProps & {
  level?: 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  color?:
    | 'inherit'
    | 'high_emphasis'
    | 'medium_emphasis'
    | 'low_emphasis'
    | 'primary'
    | 'error';
  noWrap?: boolean;
};

/**
 * Heading - Various levels of typographic hierarchies.
 *
 * ```tsx
 * <Heading level={1} noWrap>Dolore veniam</Heading>
 * ```
 */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  (props, ref) => {
    const {
      className,
      color = 'high_emphasis',
      level = 2,
      noWrap = false,
      ...rest
    } = props;

    const classes = headingVariants({
      className,
      level,
      color,
      noWrap,
    });

    return <RACHeading ref={ref} {...rest} className={classes} />;
  },
);
